/**
 * The Morning Dispatch — a period newspaper composed from the events the player's staff learned
 * about since the last edition. It is the nation's own paper: victories are trumpeted, defeats
 * become "readjustments of the front", and anything touching the signals service is withheld by
 * the censor. Quiet days get filler from the home front.
 *
 * The editorial logic (what makes a story, what leads) is shared; the wording comes from the
 * catalogue (`news.*`), so the German edition is a German paper, not a translated one.
 *
 * Pure and deterministic (no Math.random): the same events always print the same paper, which
 * keeps it testable and stable across re-renders.
 */
import {
  type FactionId,
  type GameEvent,
  type GameMap,
  type HexGrid,
  type HexId,
  hourOfDay,
  type PlayerView,
  VP_HOLD_HOURS,
} from '@friction/engine';
import { getLocale, type Locale, tr } from '../i18n';
import { cityName, unitName, unitShort } from '../i18n/names';
import { factionOf, nationOf, placePhrase, techName } from './format';
import { formatTemp, theatreShares, windFrom } from './weather';

export type NewsTone = 'good' | 'bad' | 'neutral';

export interface NewsItem {
  /** Small-caps rubric over the item ("FROM THE FRONT"). */
  kicker: string;
  head: string;
  text: string;
  tone: NewsTone;
  /** Hex to show when the item is clicked, or -1. */
  hex: HexId;
  /** Editorial weight; the heaviest story leads. */
  score: number;
}

export interface Newspaper {
  masthead: string;
  motto: string;
  day: number;
  headline: NewsItem;
  items: NewsItem[];
  /** True when nothing of note happened and the paper ran filler. */
  quiet: boolean;
}

export interface NewspaperInput {
  /** Events since the last edition (the chronicle's notable events). */
  events: readonly GameEvent[];
  /** All recorded events, to find which sides fought a battle that ended in this period. */
  history: readonly GameEvent[];
  view: PlayerView;
  map: GameMap;
  grid: HexGrid;
  day: number;
}

/** The paper is printed for this hour of the day. */
export const EDITION_HOUR = 6;

/** True if the in-game clock passed EDITION_HOUR in the ticks (before, now]. */
export function crossedEdition(before: number, now: number): boolean {
  if (now <= before) return false;
  if (now - before >= 24) return true;
  for (let t = before + 1; t <= now; t++) if (hourOfDay(t) === EDITION_HOUR) return true;
  return false;
}

/** Stable 32-bit FNV-1a hash for deterministic editorial choices. */
export function hash(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193) >>> 0;
  }
  return h;
}

/** 1-based variant number picked by a seed (the catalogue numbers its variants `…1` … `…n`). */
const variant = (count: number, seed: number): number => (seed % count) + 1;

/** Type-narrowing event filter: `events.filter(ofKind('intercept'))`. */
const ofKind =
  <K extends GameEvent['kind']>(kind: K) =>
  (e: GameEvent): e is Extract<GameEvent, { kind: K }> =>
    e.kind === kind;

const MASTHEADS = 7;
const MOTTOS = 4;
const QUIET = 5;
const FILLERS = 5;

/** Faction-flavoured masthead and motto, stable for a nation. */
export function mastheadFor(
  faction: { name: string; adjective: string },
  capital: string,
  locale: Locale = getLocale(),
): {
  masthead: string;
  motto: string;
} {
  const t = tr(locale);
  const h = hash(faction.name);
  const p = { adj: faction.adjective, capital };
  const masthead = t(`news.masthead.${variant(MASTHEADS, h) as 1 | 2 | 3 | 4 | 5 | 6 | 7}`, p);
  const motto = t(`news.motto.${variant(MOTTOS, h >>> 5) as 1 | 2 | 3 | 4}`);
  return { masthead, motto: t('news.est', { year: 1850 + (h % 40), motto }) };
}

/** The weather column: the Met Service's outlook and the state of the roads, from the view. */
export function weatherFiller(
  view: PlayerView,
  map: GameMap,
  locale: Locale = getLocale(),
): [string, string, string] {
  const t = tr(locale);
  const w = view.weather;
  const f = w.forecast;
  const shares = theatreShares(view, map);
  const temps = t('news.wx.temps', {
    min: formatTemp(f.tempMin, locale),
    max: formatTemp(f.tempMax, locale),
  });
  const kicker = t('news.kicker.weather');
  if (shares.frozen >= 0.4) return [kicker, t('news.wx.frost.head'), t('news.wx.frost', { temps })];
  if (shares.mud >= 0.2) return [kicker, t('news.wx.mud.head'), t('news.wx.mud', { temps })];
  // The worst the next twelve hours hold anywhere in the theatre.
  let rain = 0;
  let severe = 0;
  let snow = 0;
  const steps = f.leads.length;
  for (let r = 0; r < map.regions.length; r++)
    for (let s = 0; s < Math.min(4, steps); s++) {
      const i = r * steps + s;
      rain = Math.max(rain, f.precip[i]);
      severe = Math.max(severe, f.severe[i]);
      if (f.precip[i] >= 50) snow = Math.max(snow, f.snow[i]);
    }
  const dir = windFrom(w.wind);
  if (severe >= 50)
    return [
      kicker,
      t(snow >= 50 ? 'news.wx.blizzard.head' : 'news.wx.storms.head'),
      t('news.wx.storm', { dir, temps }),
    ];
  if (rain >= 60)
    return [
      kicker,
      t(snow >= 50 ? 'news.wx.snow.head' : 'news.wx.rain.head'),
      t('news.wx.rain', { dir, temps }),
    ];
  return [kicker, t('news.wx.fair.head', { dir }), t('news.wx.fair', { temps })];
}

/** Compose the paper for the morning of `day` from the events since the previous edition. */
export function composeNewspaper(input: NewspaperInput, locale: Locale = getLocale()): Newspaper {
  const t = tr(locale);
  const { events, history, view, map, grid, day } = input;
  const ctx = { map, grid };
  const me = view.faction;
  const myTeam = view.me.team;
  const teamOf = (f: FactionId) => factionOf(view, f)?.team ?? -1;
  const hostile = (f: FactionId) => teamOf(f) !== myTeam;
  const adj = (f: FactionId) => factionOf(view, f)?.adjective ?? null;
  const at = (hex: HexId) => placePhrase(ctx, hex, locale);
  // The English paper abbreviates like the staff; the German one spells designations out.
  const designation = (name: string) => (locale === 'en' ? unitShort(name, locale) : unitName(name, locale));
  const capital = map.cities[view.me.capitalCity]?.name ?? t('city.capital');
  const seed = hash(`${view.me.name}:${day}`);
  const items: NewsItem[] = [];
  const add = (item: Omit<NewsItem, 'kicker'> & { kicker?: string }) =>
    items.push({ kicker: t('news.kicker.front'), ...item });

  // ---- single events that make a story each
  for (const ev of events) {
    switch (ev.kind) {
      case 'warDeclared':
        add({
          kicker: t('news.kicker.special'),
          head: t('news.war.head'),
          text: t('news.war', { theatre: ev.theatre }),
          tone: 'neutral',
          hex: -1,
          score: 70,
        });
        break;
      case 'capitulated': {
        if (ev.faction === me) break;
        const ally = !hostile(ev.faction);
        add({
          kicker: t('news.kicker.war'),
          head: t('news.capitulates.head', { nation: nationOf(view, ev.faction) }),
          text: ally
            ? t('news.capitulates.ally', { by: nationOf(view, ev.by) })
            : t('news.capitulates.enemy', { byUs: ev.by === me, adj: adj(ev.by) }),
          tone: ally ? 'bad' : 'good',
          hex: -1,
          score: 100,
        });
        break;
      }
      case 'cityCaptured': {
        const city = map.cities[ev.city];
        if (!city) break;
        const n = variant(3, city.id) as 1 | 2 | 3;
        if (ev.to === me) {
          // Only the loser's own capital makes the bells ring.
          const cap = city.capitalOf === ev.from;
          add({
            head: t(`news.cityOurs.${n}`, { city: cityName(city.name, t.locale) }),
            text: cap
              ? t('news.cityOurs.capital', { adj: adj(ev.from) })
              : t('news.cityOurs.town', { adj: adj(ev.from), size: city.size }),
            tone: 'good',
            hex: city.hex,
            score: 30 + city.size * 12 + (cap ? 40 : 0),
          });
        } else if (ev.from === me) {
          const cap = city.id === view.me.capitalCity;
          add({
            head: t(`news.cityLost.${n}`, { city: cityName(city.name, t.locale) }),
            text: t(cap ? 'news.cityLost.capital' : 'news.cityLost.town'),
            tone: 'bad',
            hex: city.hex,
            score: 28 + city.size * 12 + (cap ? 50 : 0),
          });
        } else {
          add({
            kicker: t('news.kicker.abroad'),
            head: t('news.cityAbroad.head', { city: cityName(city.name, t.locale), adj: adj(ev.to) }),
            text: t('news.cityAbroad', {
              size: city.size,
              from: nationOf(view, ev.from),
              to: nationOf(view, ev.to),
            }),
            tone: 'neutral',
            hex: city.hex,
            score: 10 + city.size * 5,
          });
        }
        break;
      }
      case 'hqLost':
        if (ev.faction === me)
          add({ head: t('news.hqOurs.head'), text: t('news.hqOurs'), tone: 'bad', hex: -1, score: 30 });
        else if (hostile(ev.faction))
          add({
            head: t('news.hqTheirs.head'),
            text: t('news.hqTheirs', { adj: adj(ev.faction) }),
            tone: 'good',
            hex: -1,
            score: 34,
          });
        break;
      case 'techResearched':
        add({
          kicker: t('news.kicker.science'),
          head: t('news.tech.head', { tech: techName(ev.tech, locale) }),
          text: t(`tech.${ev.tech}.desc`),
          tone: 'good',
          hex: -1,
          score: 9,
        });
        break;
      case 'decoyExposed':
        // Our own dummies being exposed is a secret; the enemy's make good copy.
        if (!view.units.some((u) => u.id === ev.unit))
          add({
            kicker: t('news.kicker.curiosities'),
            head: t('news.decoy.head'),
            text: t('news.decoy', { at: at(ev.hex) }),
            tone: 'good',
            hex: ev.hex,
            score: 13,
          });
        break;
      default:
        break;
    }
  }

  // ---- aggregated stories: one item per kind of news, led by its biggest instance
  const pockets = events.filter(ofKind('encircled'));
  const ourPockets = pockets.filter((e) => e.faction === me);
  const theirPockets = pockets.filter((e) => e.faction !== me && hostile(e.faction));
  const biggest = (list: typeof pockets) => list.reduce((a, b) => (b.units.length > a.units.length ? b : a));
  if (ourPockets.length) {
    const lead = biggest(ourPockets);
    const n = ourPockets.reduce((a, e) => a + e.units.length, 0);
    add({
      head: t('news.stand.head', { at: at(lead.hex) }),
      text: t('news.stand', { n, pockets: ourPockets.length }),
      tone: 'bad',
      hex: lead.hex,
      score: 46 + 6 * n,
    });
  }
  if (theirPockets.length) {
    const lead = biggest(theirPockets);
    const n = theirPockets.reduce((a, e) => a + e.units.length, 0);
    add({
      head: t('news.trapped.head', { n, adj: adj(lead.faction), at: at(lead.hex) }),
      text: t('news.trapped', { pockets: theirPockets.length }),
      tone: 'good',
      hex: lead.hex,
      score: 50 + 6 * n,
    });
  }

  const intercepts = events.filter(ofKind('intercept'));
  const couriers = intercepts.filter((e) => e.intercept.method === 'captured-courier');
  if (couriers.length)
    add({
      kicker: t('news.kicker.intel'),
      head: t('news.couriers.head', { n: couriers.length }),
      text: t('news.couriers'),
      tone: 'good',
      hex: couriers[couriers.length - 1].intercept.order.target,
      score: 18 + 6 * couriers.length,
    });
  if (intercepts.length > couriers.length)
    add({
      kicker: t('news.kicker.intel'),
      head: t('news.withheld.head'),
      text: t('news.withheld'),
      tone: 'good',
      hex: -1,
      score: 16 + 3 * (intercepts.length - couriers.length),
    });

  const destroyed = events.filter(ofKind('unitDestroyed'));
  const enemyLost = destroyed.filter((e) => hostile(e.faction));
  const ownLost = destroyed.filter((e) => e.faction === me);
  if (enemyLost.length) {
    const last = enemyLost[enemyLost.length - 1];
    const prisoners = enemyLost.some((e) => e.cause === 'surrender');
    const n = enemyLost.length;
    add({
      head: prisoners
        ? t('news.enemyLost.prisoners')
        : n === 1
          ? t('news.enemyLost.one', { at: at(last.hex) })
          : t('news.enemyLost.many', { n }),
      text: t('news.enemyLost', {
        n,
        unit: designation(last.unitName),
        surrendered: prisoners && last.cause === 'surrender',
      }),
      tone: 'good',
      hex: last.hex,
      score: 14 + 7 * n,
    });
  }
  if (ownLost.length) {
    const last = ownLost[ownLost.length - 1];
    add({
      head: t(ownLost.length === 1 ? 'news.ownLost.one' : 'news.ownLost.many'),
      text: t('news.ownLost', { unit: designation(last.unitName), at: at(last.hex) }),
      tone: 'bad',
      hex: last.hex,
      score: 12 + 6 * ownLost.length,
    });
  }

  // Battles we fought: who attacked is only in the matching battleStarted event.
  const sides = new Map<number, { attacker: FactionId; defender: FactionId }>();
  for (const e of history.filter(ofKind('battleStarted')))
    sides.set(e.battle, { attacker: e.attackerFaction, defender: e.defenderFaction });
  let won = 0;
  let lost = 0;
  let wonHex = -1;
  let lostHex = -1;
  for (const e of events.filter(ofKind('battleEnded'))) {
    const s = sides.get(e.battle);
    if (!s || (s.attacker !== me && s.defender !== me)) continue;
    const ours = (s.attacker === me) === (e.winner === 'attacker');
    if (ours) {
      won++;
      wonHex = e.hex;
    } else {
      lost++;
      lostHex = e.hex;
    }
  }
  if (won + lost > 0)
    add(
      won >= lost
        ? {
            head: t('news.won.head', { at: at(wonHex) }),
            text: t('news.won', { won, lost }),
            tone: 'good',
            hex: wonHex,
            score: 12 + 5 * (won + lost),
          }
        : {
            head: t('news.lostBattles.head', { at: at(lostHex) }),
            text: t('news.lostBattles', { n: lost }),
            tone: 'bad',
            hex: lostHex,
            score: 12 + 5 * (won + lost),
          },
    );
  else {
    const started = events
      .filter(ofKind('battleStarted'))
      .filter((e) => e.attackerFaction === me || e.defenderFaction === me);
    if (started.length) {
      const last = started[started.length - 1];
      add({
        head: t('news.flares.head', { at: at(last.hex) }),
        text: t('news.flares', { n: started.length }),
        tone: 'neutral',
        hex: last.hex,
        score: 10 + 3 * started.length,
      });
    }
  }

  const raised = events.filter(ofKind('unitProduced'));
  if (raised.length) {
    const last = raised[raised.length - 1];
    const city = map.cities[last.city];
    add({
      kicker: t('news.kicker.home'),
      head:
        raised.length === 1
          ? t('news.raised.one', { type: last.unitType })
          : t('news.raised.many', { n: raised.length }),
      text: t('news.raised', { city: city ? cityName(city.name, t.locale) : null }),
      tone: 'good',
      hex: city?.hex ?? -1,
      score: 5 + 2 * raised.length,
    });
  }

  // The scoreboard is public: a victory clock running is front-page news.
  const clock = view.victoryClock;
  if (clock) {
    const h = Math.max(0, VP_HOLD_HOURS - (view.tick - clock.since));
    const ours = clock.team === myTeam;
    add({
      kicker: t('news.kicker.war'),
      head: t(ours ? 'news.clockOurs.head' : 'news.clockTheirs.head'),
      text: t(ours ? 'news.clockOurs' : 'news.clockTheirs', { h }),
      tone: ours ? 'good' : 'bad',
      hex: -1,
      score: ours ? 60 : 58,
    });
  }

  // ---- edit: heaviest story leads, filler keeps the page full
  items.sort((a, b) => b.score - a.score);
  const quiet = items.length === 0;
  const headline: NewsItem = quiet
    ? (() => {
        const n = variant(QUIET, seed) as 1 | 2 | 3 | 4 | 5;
        return {
          kicker: t('news.kicker.front'),
          head: t(`news.quiet.${n}.head`),
          text: t(`news.quiet.${n}.text`),
          tone: 'neutral',
          hex: -1,
          score: 0,
        };
      })()
    : items[0];
  const rest = items.slice(quiet ? 0 : 1, quiet ? 0 : 4);
  const fillers: [string, string, string][] = [
    weatherFiller(view, map, locale),
    ...([1, 2, 3, 4, 5] as const).map((n): [string, string, string] => [
      t(`news.filler.${n}.kicker`),
      t(`news.filler.${n}.head`),
      t(`news.filler.${n}.text`),
    ]),
  ];
  for (let i = 0; rest.length < 2; i++) {
    const [kicker, head, text] = fillers[((seed >>> 3) + i) % (FILLERS + 1)];
    if (!rest.some((r) => r.head === head))
      rest.push({ kicker, head, text, tone: 'neutral', hex: -1, score: 0 });
  }
  const { masthead, motto } = mastheadFor(view.me, capital, locale);
  return { masthead, motto, day, headline, items: rest, quiet };
}
